import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import AppLayoutCommon from '@lib/layouts/AppLayout/AppLayout';
import useIsOnline from '@lib/hooks/useIsOnline';
import useDeepStableValue from '@lib/hooks/useDeepStableValue';
import {
  DebugModeProvider,
  useNavigationLogger,
  usePlanTransitionLogger,
} from '@lib/services/logger';
import PlatformSwitcher, {
  Platforms,
} from '@lib/components/PlatformSwitcher/PlatformSwitcher';
import {
  checkCompanyPlanAccessByComponent,
  checkUserAccessByComponent,
  checkUserAccessForPage,
  checkUserAccessItemIdsByComponent,
} from '@lib/utils/accesses';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import TaskSyncTracker from 'components/TaskSyncTracker/TaskSyncTracker';
import {
  Access,
  AccessComponentEnum,
  AccessLevelEnum,
  UserRoleEnum,
  useAuthenticationSessionEndMutation,
} from 'graphql-common';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { OfflineTracker } from '@lib/components/OfflineTracker/OfflineTracker';
import toast from '@lib/components/Toast/Toast';
import { USER_SELECTED_SCANNER_ACTION } from '@lib/enums/localStorageKeys';
import { useTechnicalPauseContext } from '@lib/contexts/TechnicalPauseContext';
import { ScannerActionType } from 'routes/Scanner/enums';
import QrcodeScannerContainer from 'routes/Scanner/QrcodeScannerContainer';
import { removeIndexedDB } from 'utils/indexedDBUtils';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { APP_URLS } from 'constants/urls';
import { resetLogoLoaderForTab } from '@lib/components/LogoLoader/useLogoLoader';
import getHomePageUrl from './utils/getHomePageUrl';
import getNavItems from './utils/getNavItems';
import styles from './AppLayout.module.scss';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

// Apollo's cache can't normalize these (their elements have no `id`), so it
// replaces the whole array on every whoami write — a fresh `[]` here would
// otherwise still be a new reference every render once defaulted.
const EMPTY_ACCESSES: Access[] = [];

export default function AppLayout(): React.ReactElement {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const apolloClient = useApolloClient();
  const [tasksSyncLoading, setTaskSyncLoading] = useState(false);
  const [isScannerVisible, setScannerVisible] = useState(false);
  const isOnline = useIsOnline();

  // Reads ProtectedRoute's single whoami query result — must not run its own
  // independent query here (see useWhoamiContext for why).
  const { currentUser: rawCurrentUser } = useWhoamiContext();
  // See admin's DashboardLayout for why `currentUser` itself (not just its
  // unnormalized array fields) needs stabilizing.
  const currentUser = useDeepStableValue(rawCurrentUser);
  const finalAccesses =
    useDeepStableValue(currentUser?.finalAccesses) ?? EMPTY_ACCESSES;
  // Left undefined (not defaulted to EMPTY_ACCESSES) until whoami actually
  // resolves — usePlanTransitionLogger below treats an early `[]` as a real
  // "initial snapshot" and would otherwise log a bogus empty-plan entry
  // before the first real fetch completes.
  const companyPlanAccesses = useDeepStableValue(
    currentUser?.company?.plan?.accesses,
  );
  const { upcomingTechnicalPause } = useTechnicalPauseContext();
  const isFullAdmin = currentUser?.role === UserRoleEnum.FullAdmin;

  // Debug mode logging
  useNavigationLogger();
  usePlanTransitionLogger({
    accesses: companyPlanAccesses,
  });

  const getUserAccessByUrl = useCallback(
    (pathnameStr: string) => checkUserAccessForPage(finalAccesses, pathnameStr),
    [finalAccesses],
  );
  const getUserAccessByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessByComponent(finalAccesses, component),
    [finalAccesses],
  );
  const getUserAccessItemIdsByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessItemIdsByComponent(finalAccesses, component),
    [finalAccesses],
  );
  const getUserAccessByCompany = useCallback(
    (component: AccessComponentEnum) =>
      checkCompanyPlanAccessByComponent(
        companyPlanAccesses ?? EMPTY_ACCESSES,
        component,
      ),
    [companyPlanAccesses],
  );
  const logoUrl = getHomePageUrl(finalAccesses);

  const callbackOnLogout = async () => {
    localStorage.clear();
    resetLogoLoaderForTab();
    await removeIndexedDB();
    window.location.href = AUTH_PLATFORM;
  };

  const [logoutMutation, logoutMutationResult] =
    useAuthenticationSessionEndMutation({
      onCompleted: () => {
        apolloClient
          .clearStore()
          .catch(() => undefined)
          .then(callbackOnLogout);
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
        callbackOnLogout();
      },
    });

  const openScanner = useCallback(
    (newScannerActionType?: ScannerActionType) => {
      if (newScannerActionType) {
        localStorage.setItem(
          USER_SELECTED_SCANNER_ACTION,
          newScannerActionType,
        );
      }
      setScannerVisible(true);
    },
    [],
  );
  const closeScanner = useCallback(() => setScannerVisible(false), []);

  const onLogoutHandler = useCallback(
    () => logoutMutation({ variables: { input: {} } }),
    [logoutMutation],
  );

  const navItems = useMemo(
    () =>
      getNavItems({
        closeScanner,
        getUserAccessByCompany,
        getUserAccessByComponent,
        isFullAdmin,
        isOnline,
        isScannerVisible,
        openScanner,
        pathname,
        t,
      }),
    [
      closeScanner,
      getUserAccessByCompany,
      getUserAccessByComponent,
      isFullAdmin,
      isOnline,
      isScannerVisible,
      openScanner,
      pathname,
      t,
    ],
  );

  // TaskSyncTracker fetches the current user's tasks in the background — for
  // a user without Tasks access, the backend rejects those queries as
  // unauthorized, so it must stay hidden the same way the Tasks nav item does
  // (see getNavItems' isTasksAvailable).
  const isTasksAvailable =
    getUserAccessByComponent(AccessComponentEnum.TasksPerforming) !==
    AccessLevelEnum.LimitedRead;

  // Holds JSX, so an unmemoized literal here would remount nothing but would
  // still hand `AppLayoutCommon` a new prop on every render — the one prop
  // left that would defeat a `React.memo` gate on that layout.
  const components = useMemo(
    () => ({
      topContent: (
        <div className={styles.stickyTopContent}>
          <OfflineTracker>
            <SupportiveMessage
              color={SupportiveMessageColor.Yellow}
              text={t('you-are-offline')}
              leftIcon="wifi_off"
              type={SupportiveMessageType.FilledOnly}
              className={styles.stickyTopContentOfflineMessage}
            />
          </OfflineTracker>
          {isTasksAvailable && (
            <TaskSyncTracker
              isViewLocked={
                getUserAccessByCompany(
                  AccessComponentEnum.DocumentationManagement,
                ) === AccessLevelEnum.LimitedRead
              }
              setTaskSyncLoading={setTaskSyncLoading}
            />
          )}
        </div>
      ),
    }),
    [getUserAccessByCompany, isTasksAvailable, t],
  );

  const tools = useMemo(
    () => (
      <PlatformSwitcher selected={Platforms.User} available={Platforms.Admin} />
    ),
    [],
  );

  const outletContext = useMemo(
    () => ({
      closeScanner,
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      isFullAdmin,
      logoutLoading: logoutMutationResult.loading,
      onLogoutHandler,
      openScanner,
      tasksSyncLoading,
      user: currentUser,
    }),
    [
      closeScanner,
      currentUser,
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      isFullAdmin,
      logoutMutationResult.loading,
      onLogoutHandler,
      openScanner,
      tasksSyncLoading,
    ],
  );

  useEffect(() => {
    setScannerVisible(false);
  }, [pathname]);

  return (
    <DebugModeProvider
      debugModeUntil={currentUser?.debugModeUntil}
      userId={currentUser?.id}
      userEmail={currentUser?.email}
    >
      <AppLayoutCommon
        components={components}
        navItems={navItems}
        currentUser={currentUser}
        profileUrl={APP_URLS.app.profile.path}
        logoUrl={logoUrl}
        tools={tools}
        outletContext={outletContext}
        upcomingTechnicalPause={upcomingTechnicalPause}
      />
      {isScannerVisible && (
        <QrcodeScannerContainer
          closeScanner={closeScanner}
          getUserAccessByCompany={getUserAccessByCompany}
          getUserAccessByComponent={getUserAccessByComponent}
        />
      )}
    </DebugModeProvider>
  );
}
